import React from 'react';
import { Box, Typography, Button, CircularProgress, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ShipmentImageButton } from './ShipmentImage';

const UNZUORDENBAR_KDNR = 15286;

const FIELDS = [
    { key: 'type',        label: '' },
    { key: 'mpsid',       label: 'MPSID' },
    { key: 'kdnr',        label: 'Kdnr' },
    { key: 'name',        label: 'Name' },
    { key: 'absender',    label: 'Absender' },
    { key: 'sn',          label: 'Sendungsnr.' },
    { key: 'klasse',      label: 'Klasse' },
    { key: 'datum',       label: 'Datum' },
    { key: 'spedition',   label: 'Spedition' },
    { key: 'empfaenger',  label: 'Empfänger' },
    { key: 'kommentar',   label: 'Kommentar' },
    { key: 'bild',        label: 'Bild' },
    { key: 'action',      label: '' },
];

const ROW_HEIGHT = 40;
const COL_WIDTH = 260;

// match: 'full' = green, 'partial' = orange, falsy = default grey
const LegendCell = ({ children }) => (
    <Box sx={{
        height: ROW_HEIGHT, display: 'flex', alignItems: 'center',
        px: 2, fontSize: 12, fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap',
    }}>{children}</Box>
);

const DataCell = ({ children, match, mono }) => {
    const isFull = match === 'full' || match === true;
    const isPartial = match === 'partial';
    return (
        <Box sx={{
            height: ROW_HEIGHT, display: 'flex', alignItems: 'center', px: 2,
            fontSize: 13, fontFamily: mono ? 'monospace' : 'inherit',
            fontWeight: isFull || isPartial ? 600 : 400,
            color: isFull ? 'success.dark' : isPartial ? 'warning.dark' : 'text.primary',
            bgcolor: isFull ? 'success.lighter' : isPartial ? 'warning.lighter' : 'transparent',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{children}</Box>
    );
};

const ComparisonView = ({ claim, shipments, approvingId, done, onApprove, onImageClick }) => {
    return (
        <Box sx={{ display: 'flex', overflow: 'hidden', pb: 3 }}>
            {/* ── Legend column ── */}
            <Box sx={{ flexShrink: 0, borderRight: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                {FIELDS.map((f) => (
                    <LegendCell key={f.key}>{f.label}</LegendCell>
                ))}
            </Box>

            {/* ── Claim reference column ── */}
            <Box sx={{ flexShrink: 0, width: COL_WIDTH, borderRight: 2, borderColor: 'primary.light', bgcolor: 'primary.50' }}>
                <DataCell>
                    <Chip label="Suchauftrag" size="small" color="primary" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
                </DataCell>
                <DataCell mono style={{ color: '#999' }}>–</DataCell>
                <DataCell mono><strong>{claim.kdnr}</strong></DataCell>
                <DataCell><strong>{claim.kundenName || '–'}</strong></DataCell>
                <DataCell><strong>{claim.absender}</strong></DataCell>
                <DataCell mono><strong>{(claim.suchbegriffe || []).join(', ')}</strong></DataCell>
                <DataCell style={{ color: '#999' }}>–</DataCell>
                <DataCell>{claim.zustelldatum || '–'}</DataCell>
                <DataCell>{claim.spedition || '–'}</DataCell>
                <DataCell>{claim.empfaenger || '–'}</DataCell>
                <DataCell>{claim.kommentar || '–'}</DataCell>
                <DataCell />
                <DataCell />
            </Box>

            {/* ── Result columns (scrollable) ── */}
            <Box sx={{ display: 'flex', overflowX: 'auto', flex: 1 }}>
                {shipments.map((s, idx) => {
                    const unz = s.kdnr === UNZUORDENBAR_KDNR;

                    // Absender match logic: full, partial, or none
                    let absMatch = null;
                    if (s.absender && claim.absender) {
                        const sLow = s.absender.toLowerCase();
                        const cLow = claim.absender.toLowerCase();
                        if (sLow === cLow) {
                            absMatch = 'full';
                        } else if (sLow.includes(cLow) || cLow.includes(sLow)) {
                            absMatch = 'partial';
                        }
                    }

                    const snMatch = (claim.suchbegriffe || []).includes(s.sendungsnummer) ? 'full' : null;

                    return (
                        <Box key={s.id} sx={{
                            flexShrink: 0, width: COL_WIDTH,
                            borderRight: 1, borderColor: 'divider',
                        }}>
                            {/* Type */}
                            <DataCell>
                                <Chip
                                    label={unz ? 'Unzuordenbar' : 'Fremdkunde'}
                                    size="small"
                                    color={unz ? 'success' : 'warning'}
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: 11 }}
                                />
                            </DataCell>
                            {/* MPSID */}
                            <DataCell mono>{s.id}</DataCell>
                            {/* Kdnr */}
                            <DataCell mono>
                                {s.kdnr}
                                {!unz && <Typography component="span" sx={{ fontSize: 10, color: 'error.main', ml: 0.5 }}>≠ {claim.kdnr}</Typography>}
                            </DataCell>
                            {/* Name */}
                            <DataCell style={{ color: '#999' }}>–</DataCell>
                            {/* Absender */}
                            <DataCell match={absMatch}>{s.absender}</DataCell>
                            {/* SN */}
                            <DataCell match={snMatch} mono>{s.sendungsnummer}</DataCell>
                            {/* Klasse */}
                            <DataCell>{s.klasse}</DataCell>
                            {/* Datum */}
                            <DataCell>{s.creationdate || '–'}</DataCell>
                            {/* Spedition */}
                            <DataCell>{s.spedition || '–'}</DataCell>
                            {/* Empfänger */}
                            <DataCell>{s.empfaenger || '–'}</DataCell>
                            {/* Kommentar */}
                            <DataCell style={{ color: '#999' }}>–</DataCell>
                            {/* Bild */}
                            <Box sx={{ height: ROW_HEIGHT, display: 'flex', alignItems: 'center', px: 2 }}>
                                <ShipmentImageButton hasImage={!!s.imageUrl} onClick={() => onImageClick && onImageClick(idx)} />
                            </Box>
                            {/* Action */}
                            <Box sx={{ height: ROW_HEIGHT, display: 'flex', alignItems: 'center', px: 1.5 }}>
                                <Button size="small" variant="contained" disableElevation
                                    color={unz ? 'success' : 'warning'}
                                    sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600 }}
                                    startIcon={approvingId === s.id ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => onApprove && onApprove(s)}
                                    disabled={approvingId !== null || done}>
                                    Freigeben
                                </Button>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default ComparisonView;
